// ========== Java Question Bank ==========

export const javaQuestionBank = [
    // ========== Datatypes ==========
    {
        question: "Which datatype is used to store a single character in Java?",
        options: ["String", "char", "Character", "byte"],
        answer: "char",
        explanation: "`char` stores a single 16-bit Unicode character.",
        link: "/java-datatypes",
        category: "Datatypes",
    },
    {
        question: "What is the default value of a boolean in Java?",
        options: ["true", "false", "null", "0"],
        answer: "false",
        explanation: "Booleans default to `false` in Java.",
        link: "/java-datatypes",
        category: "Datatypes",
    },
    {
        question: "Which datatype is used for decimal numbers?",
        options: ["int", "float", "char", "boolean"],
        answer: "float",
        explanation: "`float` and `double` are used for decimal values.",
        link: "/java-datatypes",
        category: "Datatypes",
    },

    // ========== Variables ==========
    {
        question: "Which keyword is used to declare a constant variable?",
        options: ["const", "final", "static", "immutable"],
        answer: "final",
        explanation: "`final` prevents reassignment of a variable.",
        link: "/java-variables",
        category: "Variables",
    },
    {
        question: "Which of the following is a valid variable name?",
        options: ["2value", "value$", "class", "void"],
        answer: "value$",
        explanation:
            "Variable names cannot start with digits or use reserved keywords.",
        link: "/java-variables",
        category: "Variables",
    },
    {
        question: "What is the scope of a local variable?",
        options: ["Global", "Class-wide", "Method-wide", "Block-level"],
        answer: "Block-level",
        explanation:
            "Local variables are accessible only within the block they are defined.",
        link: "/java-variables",
        category: "Variables",
    },

    // ========== Control Structures ==========
    {
        question: "Which loop is guaranteed to execute at least once?",
        options: ["for", "while", "do-while", "foreach"],
        answer: "do-while",
        explanation: "`do-while` executes the loop body before checking the condition.",
        link: "/java-control-structures",
        category: "Control Structures",
    },
    {
        question: "Which keyword is used to exit a loop prematurely?",
        options: ["continue", "exit", "break", "return"],
        answer: "break",
        explanation: "`break` exits the loop immediately.",
        link: "/java-control-structures",
        category: "Control Structures",
    },
    {
        question: "What does the `continue` statement do?",
        options: [
            "Exits the loop",
            "Skips the current iteration",
            "Ends the method",
            "Throws an exception",
        ],
        answer: "Skips the current iteration",
        explanation: "`continue` skips to the next iteration of the loop.",
        link: "/java-control-structures",
        category: "Control Structures",
    },

    // ========== Methods ==========
    {
        question: "Which keyword is used to define a method?",
        options: ["function", "method", "def", "void"],
        answer: "void",
        explanation: "`void` is used when a method does not return a value.",
        link: "/java-methods",
        category: "Methods",
    },
    {
        question: "Which part of a method defines its return type?",
        options: ["Parameters", "Body", "Signature", "Declaration"],
        answer: "Declaration",
        explanation: "The method declaration includes the return type.",
        link: "/java-methods",
        category: "Methods",
    },
    {
        question: "What is method overloading?",
        options: [
            "Using multiple classes",
            "Defining methods with same name but different parameters",
            "Calling a method recursively",
            "Using inheritance",
        ],
        answer: "Defining methods with same name but different parameters",
        explanation:
            "Overloading allows multiple methods with the same name but different signatures.",
        link: "/java-methods",
        category: "Methods",
    },

    // ========== Scopes ==========
    {
        question: "Which scope applies to static variables?",
        options: ["Instance", "Method", "Class", "Block"],
        answer: "Class",
        explanation: "Static variables belong to the class, not instances.",
        link: "/java-scopes",
        category: "Scopes",
    },
    {
        question: "Which scope does a parameter variable have?",
        options: ["Global", "Method", "Class", "Package"],
        answer: "Method",
        explanation: "Parameters are scoped to the method they are declared in.",
        link: "/java-scopes",
        category: "Scopes",
    },
    {
        question: "What is the scope of a public field?",
        options: ["Package", "Class", "Global", "Accessible from anywhere"],
        answer: "Accessible from anywhere",
        explanation: "`public` fields can be accessed from any class.",
        link: "/java-scopes",
        category: "Scopes",
    },

    // ========== OOP ==========
    {
        question: "Which keyword is used to inherit a class?",
        options: ["implements", "extends", "inherits", "super"],
        answer: "extends",
        explanation: "`extends` is used for class inheritance.",
        link: "/java-oop",
        category: "OOP",
    },
    {
        question: "What is encapsulation?",
        options: [
            "Hiding internal state and requiring access through methods",
            "Using multiple classes",
            "Overloading methods",
            "Inheriting from multiple classes",
        ],
        answer: "Hiding internal state and requiring access through methods",
        explanation: "Encapsulation protects object state using access modifiers.",
        link: "/java-oop",
        category: "OOP",
    },
    {
        question: "Which keyword refers to the current object?",
        options: ["this", "super", "self", "object"],
        answer: "this",
        explanation: "`this` refers to the current instance of the class.",
        link: "/java-oop",
        category: "OOP",
    },

    // ========== Arrays and Strings ==========
    {
        question: "Which method returns the length of a string in Java?",
        options: ["size()", "length()", "getLength()", "count()"],
        answer: "length()",
        explanation: "`length()` returns the number of characters in a string.",
        link: "/java-strings",
        category: "Arrays and Strings",
    },
    {
        question: "How do you declare an array of integers?",
        options: ["int[] arr;", "int arr[];", "int arr();", "array<int> arr;"],
        answer: "int[] arr;",
        explanation: "Java uses `int[]` syntax to declare arrays.",
        link: "/java-arrays",
        category: "Arrays and Strings",
    },
    {
        question: "Which method is used to compare two strings?",
        options: ["==", "equals()", "compare()", "match()"],
        answer: "equals()",
        explanation: "`equals()` compares string contents, not references.",
        link: "/java-strings",
        category: "Arrays and Strings",
    },

    // ========== Exception Handling ==========
    {
        question: "Which keyword is used to handle exceptions?",
        options: ["try", "catch", "throw", "All of the above"],
        answer: "All of the above",
        explanation: "`try`, `catch`, and `throw` are all part of exception handling.",
        link: "/java-exceptions",
        category: "Exception Handling",
    },
    {
        question: "Which class is the superclass of all exceptions?",
        options: ["Throwable", "Error", "Exception", "RuntimeException"],
        answer: "Throwable",
        explanation: "`Throwable` is the root class for all errors and exceptions.",
        link: "/java-exceptions",
        category: "Exception Handling",
    },
    {
        question: "What does the `finally` block do?",
        options: [
            "Handles exceptions",
            "Executes only if an exception occurs",
            "Executes regardless of exception",
            "Terminates the program",
        ],
        answer: "Executes regardless of exception",
        explanation:
            "`finally` always runs after `try` and `catch`, even if an exception is thrown.",
        link: "/java-exceptions",
        category: "Exception Handling",
    },

    // ========== File Handling ==========
    {
        question: "Which class is used to read text files?",
        options: ["FileReader", "Scanner", "BufferedReader", "All of the above"],
        answer: "All of the above",
        explanation: "Each class can read files in different ways.",
        link: "/java-file-handling",
        category: "File Handling",
    },
    {
        question: "Which package contains file handling classes?",
        options: ["java.io", "java.util", "java.file", "java.fs"],
        answer: "java.io",
        explanation: "`java.io` provides classes for file input/output.",
        link: "/java-file-handling",
        category: "File Handling",
    },
    {
        question: "Which class is used to write to a file?",
        options: ["FileWriter", "PrintWriter", "BufferedWriter", "All of the above"],
        answer: "All of the above",
        explanation: "Each class offers different ways to write to files.",
        link: "/java-file-handling",
        category: "File Handling",
    },

    // ========== Collections ==========
    {
        question: "Which interface does ArrayList implement?",
        options: ["List", "Set", "Map", "Queue"],
        answer: "List",
        explanation: "`ArrayList` implements the `List` interface.",
        link: "/java-collections",
        category: "Collections",
    },
    {
        question: "Which collection does not allow duplicate elements?",
        options: ["List", "Set", "Map", "Queue"],
        answer: "Set",
        explanation: "`Set` ensures all elements are unique.",
        link: "/java-collections",
        category: "Collections",
    },
    {
        question: "Which class stores key-value pairs?",
        options: ["List", "Set", "Map", "Queue"],
        answer: "Map",
        explanation: "`Map` stores data as key-value pairs.",
        link: "/java-collections",
        category: "Collections",
    },

    // ========== Interfaces and Abstraction ==========
    {
        question: "Which keyword is used to implement an interface?",
        options: ["extends", "implements", "inherits", "interface"],
        answer: "implements",
        explanation: "`implements` is used to define interface behavior in a class.",
        link: "/java-interfaces",
        category: "Interfaces and Abstraction",
    },
    {
        question: "Can an interface contain method implementations?",
        options: ["No", "Yes, using default methods", "Only static methods", "Only abstract methods"],
        answer: "Yes, using default methods",
        explanation: "Java 8 introduced default methods in interfaces.",
        link: "/java-interfaces",
        category: "Interfaces and Abstraction",
    },
    {
        question: "What is the purpose of an abstract class?",
        options: [
            "To create objects",
            "To define common behavior without full implementation",
            "To inherit multiple classes",
            "To override static methods",
        ],
        answer: "To define common behavior without full implementation",
        explanation: "Abstract classes provide partial implementation for subclasses.",
        link: "/java-abstraction",
        category: "Interfaces and Abstraction",
    },

    // ========== Lambda Expressions ==========
    {
        question: "Which version of Java introduced lambda expressions?",
        options: ["Java 6", "Java 7", "Java 8", "Java 11"],
        answer: "Java 8",
        explanation: "Lambda expressions were introduced in Java 8.",
        link: "/java-lambda",
        category: "Lambda Expressions",
    },
    {
        question: "What is the syntax of a lambda expression?",
        options: ["(args) -> expression", "args => expression", "function(args)", "lambda(args)"],
        answer: "(args) -> expression",
        explanation: "Java uses `(args) -> expression` syntax for lambdas.",
        link: "/java-lambda",
        category: "Lambda Expressions",
    },
    {
        question: "Which interface type is required for lambda expressions?",
        options: ["Functional interface", "Abstract class", "Serializable", "Runnable"],
        answer: "Functional interface",
        explanation: "Lambdas require interfaces with a single abstract method.",
        link: "/java-lambda",
        category: "Lambda Expressions",
    },

    // ========== Multithreading ==========
    {
        question: "Which class is used to create threads?",
        options: ["Thread", "Runnable", "Executor", "Process"],
        answer: "Thread",
        explanation: "`Thread` is the base class for multithreading.",
        link: "/java-multithreading",
        category: "Multithreading",
    },
    {
        question: "Which method starts a thread?",
        options: ["run()", "start()", "execute()", "launch()"],
        answer: "start()",
        explanation: "`start()` begins thread execution by calling `run()` internally.",
        link: "/java-multithreading",
        category: "Multithreading",
    },
    {
        question: "Which keyword ensures thread safety?",
        options: ["volatile", "synchronized", "static", "final"],
        answer: "synchronized",
        explanation:
            "`synchronized` prevents concurrent access to critical sections.",
        link: "/java-multithreading",
        category: "Multithreading",
    },
];

// ========== Utilities ==========

export const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

export const getBadge = (score, total) => {
    const ratio = score / total;
    if (ratio === 1) return "🧙 Type Wizard";
    if (ratio >= 0.8) return "⚡ Type Pro";
    if (ratio >= 0.5) return "📘 Type Apprentice";
    return "🌱 Type Novice";
};

export const saveScore = (entry) => {
    const prev = JSON.parse(localStorage.getItem("ts_leaderboard") || "[]");
    const updated = [entry, ...prev].slice(0, 5);
    localStorage.setItem("ts_leaderboard", JSON.stringify(updated));
};

export const loadScores = () =>
    JSON.parse(localStorage.getItem("ts_leaderboard") || "[]");
